import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  UploadCloud, Loader2, X, Cpu, Share2, TrendingUp, TrendingDown, Target, CheckCircle2,
} from "lucide-react";

const compressImage = (file, cb) => {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const max = 1200;
      let { width, height } = img;
      if (width > max || height > max) {
        const s = max / Math.max(width, height);
        width = Math.round(width * s); height = Math.round(height * s);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      cb(canvas.toDataURL("image/jpeg", 0.82));
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
};

const buildContent = (r, note) => {
  const lines = [];
  if (note.trim()) lines.push(note.trim(), "");
  lines.push(`${r.pair || "Chart"} — Strong Verdict`);
  const head = [String(r.bias || "").toUpperCase()];
  if (r.order_type) head.push(r.order_type);
  if (r.confidence != null && r.confidence !== "") head.push(`${r.confidence}% confidence`);
  lines.push(head.filter(Boolean).join(" · "));
  const trade = [];
  if (r.entry) trade.push(`Entry ${r.entry}`);
  if (r.stop_loss) trade.push(`SL ${r.stop_loss}`);
  if (r.take_profit_1) trade.push(`TP1 ${r.take_profit_1}`);
  if (r.take_profit_2) trade.push(`TP2 ${r.take_profit_2}`);
  if (r.risk_reward) trade.push(`R:R ${r.risk_reward}`);
  if (trade.length) lines.push(trade.join(" · "));
  if (r.price_action) lines.push("", "Price Action", r.price_action);
  return lines.join("\n");
};

const Metric = ({ label, value, accent }) => (
  <div className="rounded-lg bg-[#0C1017] border border-white/8 px-3 py-2.5">
    <div className="font-data text-[9px] uppercase tracking-widest text-slate-500">{label}</div>
    <div className={`font-data text-sm mt-0.5 ${accent || "text-slate-100"}`}>{value || "—"}</div>
  </div>
);

export const AdminShareTrade = () => {
  const [image, setImage] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [result, setResult] = useState(null);
  const [note, setNote] = useState("");
  const [shared, setShared] = useState(false);
  const fileRef = useRef(null);

  const onFile = (file) => {
    if (!file) return;
    if (!/image\/(png|jpe?g|webp)/.test(file.type)) { toast.error("Attach a PNG, JPG or WEBP chart"); return; }
    compressImage(file, (data) => { setImage(data); setResult(null); setShared(false); });
  };

  const clearAll = () => {
    setImage(null); setResult(null); setShared(false); setNote("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const runVerdict = async () => {
    if (!image) { toast.error("Upload a chart first"); return; }
    setAnalyzing(true); setResult(null); setShared(false);
    try {
      const { data } = await api.post("/analyze/chart", { image_base64: image, strategy: "price_action" });
      setResult(data.result);
      toast.success("Strong verdict ready");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const shareToCommunity = async () => {
    if (!result || !image) return;
    setSharing(true);
    try {
      await api.post("/community/posts", {
        content: buildContent(result, note),
        pair: result.pair || null,
        sentiment: String(result.bias).toUpperCase() === "BULLISH" ? "Bullish"
          : String(result.bias).toUpperCase() === "BEARISH" ? "Bearish" : null,
        tag: "Analysis",
        image_base64: image,
      });
      setShared(true);
      toast.success("Shared to the community floor");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not share");
    } finally {
      setSharing(false);
    }
  };

  const bull = result && String(result.bias).toUpperCase() === "BULLISH";
  const biasCls = bull ? "text-gain" : "text-loss";

  return (
    <div data-testid="admin-share-trade" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Left: upload + verdict */}
      <div className="card-surface rounded-xl p-5 space-y-4">
        <h3 className="font-heading text-lg font-bold flex items-center gap-2"><Cpu className="h-5 w-5 text-brand" /> Chart & Price-Action Verdict</h3>

        {image ? (
          <div className="relative rounded-lg overflow-hidden border border-white/10" data-testid="admin-share-image-preview">
            <img src={image} alt="chart" className="w-full max-h-64 object-contain bg-[#0C1017]" />
            <button onClick={clearAll} data-testid="admin-share-clear-btn" className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/70 border border-white/20 grid place-items-center hover:bg-loss/80">
              <X className="h-4 w-4 text-white" />
            </button>
          </div>
        ) : (
          <button
            data-testid="admin-share-upload"
            onClick={() => fileRef.current?.click()}
            className="w-full rounded-lg border-2 border-dashed border-white/15 hover:border-brand/50 py-12 flex flex-col items-center gap-2 text-slate-400 hover:text-brand transition-colors"
          >
            <UploadCloud className="h-8 w-8" />
            <span className="text-sm font-semibold">Upload a chart to analyze</span>
            <span className="font-data text-[10px] uppercase tracking-widest">PNG · JPG · WEBP</span>
          </button>
        )}
        <input ref={fileRef} data-testid="admin-share-image-input" type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />

        <Button
          data-testid="admin-share-run-verdict-btn"
          onClick={runVerdict}
          disabled={analyzing || !image}
          className="w-full bg-brand hover:bg-brand-hover text-white font-semibold h-11 gap-2"
        >
          {analyzing ? <><Loader2 className="h-4 w-4 animate-spin" /> Analyzing…</> : <><Cpu className="h-4 w-4" /> Run Strong Verdict</>}
        </Button>
        <p className="font-data text-[10px] text-slate-500 text-center">Pure price-action read · admins have unlimited runs</p>
      </div>

      {/* Right: verdict + share */}
      <div className="card-surface rounded-xl p-5">
        <AnimatePresence mode="wait">
          {!result ? (
            <div className="h-full min-h-64 grid place-items-center text-center px-6">
              <div className="flex flex-col items-center">
                <Target className="h-8 w-8 text-slate-600 mb-3" />
                <p className="text-sm text-slate-400 max-w-xs">Run the strong verdict, then share the chart and its price-action call straight to the community.</p>
              </div>
            </div>
          ) : (
            <motion.div key="verdict" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="admin-share-verdict">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-heading text-xl font-bold">{result.pair || "Chart"}</div>
                  <div className="font-data text-[10px] uppercase tracking-widest text-slate-500">{result.timeframe || "Price Action"}</div>
                </div>
                <span className={`inline-flex items-center gap-1.5 font-data text-xs uppercase tracking-widest px-3 py-1.5 rounded-full border ${bull ? "text-gain border-gain/30 bg-gain/10" : "text-loss border-loss/30 bg-loss/10"}`} data-testid="admin-share-bias">
                  {bull ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                  {result.bias} · {result.confidence}%
                </span>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <Metric label="Order" value={result.order_type} accent={biasCls} />
                <Metric label="Entry" value={result.entry} />
                <Metric label="R : R" value={result.risk_reward} accent="text-brand" />
                <Metric label="Stop Loss" value={result.stop_loss} accent="text-loss" />
                <Metric label="TP 1" value={result.take_profit_1} accent="text-gain" />
                <Metric label="TP 2" value={result.take_profit_2} accent="text-gain" />
              </div>

              <div>
                <div className="font-data text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">Price Action</div>
                <p className="text-sm text-slate-300 leading-relaxed">{result.price_action}</p>
              </div>

              <Textarea
                data-testid="admin-share-note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add an optional note for the community (shown above the verdict)…"
                className="bg-[#0C1017] border-white/10 text-white text-sm resize-none h-20"
              />

              {shared ? (
                <div className="flex items-center justify-center gap-2 h-11 rounded-md bg-gain/10 border border-gain/30 text-gain font-data text-xs uppercase tracking-wider" data-testid="admin-share-success">
                  <CheckCircle2 className="h-4 w-4" /> Shared to community
                </div>
              ) : (
                <Button
                  data-testid="admin-share-to-community-btn"
                  onClick={shareToCommunity}
                  disabled={sharing}
                  className="w-full bg-gain hover:bg-gain/90 text-[#05231c] font-bold h-11 gap-2"
                >
                  {sharing ? <><Loader2 className="h-4 w-4 animate-spin" /> Sharing…</> : <><Share2 className="h-4 w-4" /> Share Trade with Community</>}
                </Button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
